import type { z } from "zod";
import type { ActivationLinkStatusResult, PasswordResetOtpResult } from "../domain";
import type { AuthSession, AuthUser, LoginResult } from "../domain/authSchemas";
import type {
  activationLinkResponseDtoSchema,
  AuthUserDto,
  LoginResultDto,
  passwordResetOtpResponseDtoSchema,
  SessionDto,
} from "./authDtos";

/**
 * `userType`/`isAccountAdmin` are derived from the backend's `type`, not sent
 * directly — REBUILD-PLAN §2.5 wants coarse routing collapsed to one field
 * and permission-gating done everywhere else.
 */
export function authUserFromDto(dto: AuthUserDto): AuthUser {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    userType:
      dto.type === "ADMIN" || dto.type === "EMPLOYEE" ? "staff" : "customer",
    isAccountAdmin: dto.type === "ADMIN" || dto.type === "CLIENT_ADMIN",
    // The company name is kept for the profile screen's read-only field.
    account:
      dto.account === null
        ? null
        : {
            id: dto.account.id,
            nameEn: dto.account.nameEn,
            nameAr: dto.account.nameAr,
          },
    permissions: [...dto.permissions],
  };
}

export function authSessionFromDto(dto: SessionDto): AuthSession {
  return { accessToken: dto.accessToken, user: authUserFromDto(dto.user) };
}

export function loginResultFromDto(dto: LoginResultDto): LoginResult {
  if (dto.requiresOtp) return { requiresOtp: true };
  return {
    accessToken: dto.accessToken,
    user: authUserFromDto(dto.user),
    requiresOtp: false,
  };
}

export function passwordResetOtpResultFromDto(
  response: z.infer<typeof passwordResetOtpResponseDtoSchema>,
): PasswordResetOtpResult {
  return {
    resetToken: response.data.resetToken,
    expiresAt: response.data.expiresAt,
  };
}

export function activationLinkResultFromDto(
  response: z.infer<typeof activationLinkResponseDtoSchema>,
): ActivationLinkStatusResult {
  return { ...response.data, message: response.message };
}
